import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Bell,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Info,
  Loader2,
  LogOut,
  Mail,
  Menu,
  RefreshCw,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { merchantService, type MerchantNotification } from '../services/merchant.service';
import { PesaJetLogo } from './PesaJetLogo';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const [userOpen, setUserOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<MerchantNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notificationError, setNotificationError] = useState(false);
  const polling = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await merchantService.getNotifications({ limit: 8 });
      const items = Array.isArray(response.data) ? response.data : [];
      setNotifications(items);
      setUnreadCount(
        response.pagination?.totalUnread ?? items.filter((item) => !item.isRead).length
      );
      setNotificationError(false);
    } catch {
      setNotificationError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    polling.current = setInterval(() => fetchNotifications(true), 30000);
    return () => {
      if (polling.current) clearInterval(polling.current);
    };
  }, []);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setNotificationsOpen(false);
        setUserOpen(false);
      }
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, []);

  const markRead = async (notification: MerchantNotification) => {
    if (notification.isRead) return;
    try {
      await merchantService.markNotificationAsRead(notification.id);
      setNotifications((items) =>
        items.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item))
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch {
      setNotificationError(true);
    }
  };

  const markAllRead = async () => {
    try {
      await merchantService.markAllNotificationsAsRead();
      setNotifications((items) => items.map((item) => ({ ...item, isRead: true })));
      setUnreadCount(0);
    } catch {
      setNotificationError(true);
    }
  };

  const icon = (type: MerchantNotification['type']) =>
    type === 'SUCCESS' ? (
      <CheckCircle2 />
    ) : type === 'ERROR' ? (
      <AlertCircle />
    ) : type === 'WARNING' ? (
      <AlertCircle />
    ) : (
      <Info />
    );
  const initial = (user?.businessName || user?.email || 'P').charAt(0).toUpperCase();

  return (
    <header className="app-header">
      <div className="app-header-mobile">
        <button onClick={onMenuClick} aria-label="Open navigation">
          <Menu />
        </button>
        <PesaJetLogo size="sm" variant="full" />
      </div>
      <div className={`app-mode ${user?.isSandbox ? 'sandbox' : 'live'}`}>
        <i></i>
        <span>{user?.isSandbox ? 'Sandbox mode' : 'Live mode'}</span>
      </div>
      <div className="app-actions">
        <div className="notification-control">
          <button
            className={`notification-trigger ${notificationsOpen ? 'active' : ''}`}
            onClick={() => {
              setNotificationsOpen((open) => !open);
              setUserOpen(false);
            }}
            aria-label={`${unreadCount} unread notifications`}
            aria-expanded={notificationsOpen}
          >
            <Bell />
            {unreadCount > 0 && <b>{unreadCount > 9 ? '9+' : unreadCount}</b>}
          </button>
          {notificationsOpen && (
            <>
              <button
                className="header-dismiss"
                onClick={() => setNotificationsOpen(false)}
                aria-label="Close notifications"
              />
              <section
                className="notification-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Notifications"
              >
                <header>
                  <div>
                    <span>Activity</span>
                    <h2>Notifications</h2>
                  </div>
                  <button onClick={() => setNotificationsOpen(false)} aria-label="Close">
                    <X />
                  </button>
                </header>
                <div className="notification-tools">
                  <span>{unreadCount ? `${unreadCount} unread` : 'You are up to date'}</span>
                  <div>
                    <button
                      onClick={() => fetchNotifications()}
                      disabled={loading}
                      aria-label="Refresh notifications"
                    >
                      <RefreshCw className={loading ? 'animate-spin' : ''} />
                    </button>
                    {unreadCount > 0 && (
                      <button onClick={markAllRead}>
                        <CheckCheck />
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>
                <div className="notification-list">
                  {loading && !notifications.length ? (
                    <div className="notification-state">
                      <Loader2 className="animate-spin" />
                      <strong>Loading activity</strong>
                      <p>Checking for new account updates.</p>
                    </div>
                  ) : notificationError && !notifications.length ? (
                    <div className="notification-state error">
                      <AlertCircle />
                      <strong>Could not load notifications</strong>
                      <p>Check your connection and try again.</p>
                      <button onClick={() => fetchNotifications()}>Try again</button>
                    </div>
                  ) : !notifications.length ? (
                    <div className="notification-state">
                      <Bell />
                      <strong>All caught up</strong>
                      <p>New payment and account activity will appear here.</p>
                    </div>
                  ) : (
                    notifications.map((item) => (
                      <button
                        key={item.id}
                        className={`notification-item ${item.isRead ? 'read' : 'unread'} ${item.type.toLowerCase()}`}
                        onClick={() => markRead(item)}
                      >
                        <i>{icon(item.type)}</i>
                        <span>
                          <strong>{item.title}</strong>
                          <p>{item.message}</p>
                          <small>
                            <Clock3 />
                            {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                          </small>
                        </span>
                        {!item.isRead && <b aria-label="Unread" />}
                      </button>
                    ))
                  )}
                </div>
              </section>
            </>
          )}
        </div>
        <div className="app-profile-control">
          <button
            className="app-profile"
            onClick={() => {
              setUserOpen((open) => !open);
              setNotificationsOpen(false);
            }}
            aria-expanded={userOpen}
          >
            <i>
              {initial}
              <b></b>
            </i>
            <span>
              <strong>{user?.businessName || 'Merchant account'}</strong>
              <small>{String(user?.role || 'Principal merchant').replace(/_/g, ' ')}</small>
            </span>
            <ChevronDown />
          </button>
          {userOpen && (
            <>
              <button
                className="header-dismiss"
                onClick={() => setUserOpen(false)}
                aria-label="Close account menu"
              />
              <section className="profile-menu">
                <header>
                  <span>Signed in as</span>
                  <strong>{user?.email}</strong>
                </header>
                <Link to="/settings" onClick={() => setUserOpen(false)}>
                  <Settings />
                  Account settings
                </Link>
                <Link to="/kyc" onClick={() => setUserOpen(false)}>
                  <ShieldCheck />
                  Verification
                </Link>
                <hr className="my-2 border-gray-200" />
                <a
                  href="https://chat.whatsapp.com/FDEpAAdwm5kAsd1KcgHSsg?s=cl&p=i&mlu=0&ilr=0&amv=2"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 text-pesajet-700 hover:bg-gray-50 rounded-md"
                >
                  <img src="/whatsapp.svg" alt="WhatsApp" className="w-4 h-4" />
                  WhatsApp Support
                </a>
                <a
                  href="mailto:support@pesajet.com"
                  className="flex items-center gap-2 px-4 py-2 text-pesajet-700 hover:bg-gray-50 rounded-md"
                >
                  <Mail className="w-4 h-4" />
                  Email Support
                </a>
                <hr className="my-2 border-gray-200" />
                <button onClick={logout}>
                  <LogOut />
                  Sign out
                </button>
              </section>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
